import * as React from 'react';
import type { ClientItemId } from 'owa-client-ids';
import type { FreeBusyType } from 'owa-freebusy-style';
import { AgendaFreeBusyBar } from './AgendaFreeBusyBar';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import {
    Detail,
    TextDetailPart,
    isButtonDetailPart,
    isCustomDetailPart,
} from '../schema/DetailPart';
import { DetailPartView } from './AgendaEventDetailViews';
import type { IIconProps } from '@fluentui/react/lib/Icon';
import { IconWell } from './IconWell';
import { FocusZone, FocusZoneDirection, IFocusZone } from '@fluentui/react/lib/FocusZone';
import type { KeyboardEvent } from 'react';
import { getRTLSafeKeyCode } from '@fluentui/utilities';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { getDensityModeString } from 'owa-fabric-theme';
import {
    getKeyboardCalendarEventInteraction,
    KeyboardCalendarEventInteraction,
} from 'owa-calendar-event-interaction-schema';

import styles from './AgendaEventViewBase.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface AgendaEventViewBaseProps {
    id: ClientItemId;
    onClick: (target: any) => void;
    onDoubleClick: (target: any) => void;
    onRightClick: (target: any) => void;
    onItemKeyDown: (target: any, interaction: KeyboardCalendarEventInteraction) => void;
    className?: string;
    topDetails: TopDetails;
    bottomDetails?: BottomDetail[];
    freeBusyType: FreeBusyType;
    colorScheme: ColorScheme;
    isSelected: boolean;
    isInteractive: boolean;
    ariaLabel: string;
    title: string;
    showCompact?: boolean;
    customStyles?: CustomAgendaEventViewBaseStyles;
    disableCharmColors?: boolean;
}

export interface CustomAgendaEventViewBaseStyles {
    base: {
        agendaItem: string;
        base: string;
        detailRow: string;
    };
    common: {
        subject: string;
        location: string;
        organizer: string;
        time: string;
    };
}

/**
 * These are the are used to show the detail content at the top of the agenda Item. The details are aligned with the timeDetails and the icons.
 */
export interface TopDetails {
    timeDetails: TextDetailPart[];
    details: Detail[];
    buttonDetail?: Detail;
    icons: IIconProps[];
}

/**
 * These are the details that appear after the last time or detail row (whichever is longer). The do not contain time or icon data.
 * They can span the whole agenda item (isFullDetail = true), or they can align with the details in the TopDetails (isFullDetail = false)
 */
export type BottomDetail = Detail & { isFullDetail?: boolean };

export const AgendaEventViewBase = observer(function AgendaEventViewBase(
    props: AgendaEventViewBaseProps
) {
    const focusZoneRef = React.useRef<React.RefObject<IFocusZone>>(React.createRef());
    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        switch (event.keyCode) {
            case getRTLSafeKeyCode(KeyCodes.right):
                focusZoneRef.current.current.focus(true);
                event.preventDefault();
                event.stopPropagation();
                break;
            default:
                const keyboardCalendarEventInteraction = getKeyboardCalendarEventInteraction(event);
                if (
                    keyboardCalendarEventInteraction != null &&
                    keyboardCalendarEventInteraction != KeyboardCalendarEventInteraction.Open // allow 'Enter' to propogate to onClick
                ) {
                    props.onItemKeyDown(event.currentTarget, keyboardCalendarEventInteraction);
                    event.preventDefault();
                    event.stopPropagation();
                }
                break;
        }
    };
    const {
        className,
        topDetails,
        bottomDetails,
        freeBusyType,
        colorScheme,
        isSelected,
        isInteractive,
        ariaLabel,
        title,
        onClick,
        onDoubleClick,
        onRightClick,
        showCompact,
        customStyles,
    } = props;
    const isAgendaListDensityEnabled = showCompact ?? true;
    const topDetailRows = getDetailRows(topDetails, customStyles);
    const bottomDetailRows = bottomDetails ? getBottomDetailRows(bottomDetails) : [];
    return (
        <FocusZone
            direction={FocusZoneDirection.horizontal}
            componentRef={focusZoneRef.current}
            data-is-focusable={true}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onContextMenu={onRightClick}
            className={classNames(
                styles.agendaItem,
                customStyles?.base.agendaItem,
                {
                    [styles.selected]: isSelected,
                    [styles.interactive]: isInteractive,
                },
                isAgendaListDensityEnabled && getDensityModeString(),
                className
            )}
            onKeyDown={onKeyDown}
            aria-label={ariaLabel}
            title={title}>
            <AgendaFreeBusyBar color={colorScheme} freeBusyType={freeBusyType} />
            <div
                className={classNames(
                    styles.base,
                    customStyles?.base.base,
                    isAgendaListDensityEnabled && getDensityModeString()
                )}>
                {topDetailRows.concat(bottomDetailRows)}
            </div>
        </FocusZone>
    );
});

const DetailRow = observer(function DetailRow(props: {
    detail: Detail | null;
    timeDetail: TextDetailPart | null;
    buttonDetail?: Detail;
    icons: IIconProps[] | null;
    hasButton: boolean;
    customStyles?: CustomAgendaEventViewBaseStyles;
}) {
    const detailRowStyle = getRowStylesForDetailPart(props.detail, props.customStyles);
    return (
        <div className={detailRowStyle}>
            <div className={styles.leftColumn}>
                {props.timeDetail && (
                    <div
                        title={props.timeDetail.getTextProps().text}
                        className={classNames(
                            props.timeDetail.getTextProps().className,
                            styles.timeDetail
                        )}>
                        {props.timeDetail.getTextProps().text}
                    </div>
                )}
            </div>
            {props.detail && <DetailPartView {...props.detail} />}
            {props.buttonDetail && !props.icons && <DetailPartView {...props.buttonDetail} />}
            {props.icons && !props.buttonDetail && props.icons.length > 0 && (
                <IconWell
                    icons={props.icons}
                    className={props.hasButton ? styles.iconWithButton : null}
                />
            )}
        </div>
    );
});

/**
 * The style for the row ( the container for the time, detail part, and icon components) changes depending on the detail part type.
 * When we have a custom detail part/ button detail part, we want to center align the row elements, if we have a text detail, we want to align
 * the base lines of all the elements
 */
function getRowStylesForDetailPart(
    detail: Detail | null,
    customStyles?: CustomAgendaEventViewBaseStyles
): string {
    const isCustomDetailRow = detail && (isButtonDetailPart(detail) || isCustomDetailPart(detail));
    return classNames(
        styles.detailRow,
        customStyles?.base.detailRow,
        isCustomDetailRow && styles.customDetailRow,
        getDensityModeString()
    );
}

function getDetailRows(
    topDetails: TopDetails,
    customStyles?: CustomAgendaEventViewBaseStyles
): JSX.Element[] {
    const { timeDetails, details, icons, buttonDetail: buttonDetail } = topDetails;
    const longerDetailsLength = Math.max(details.length, timeDetails.length);
    const detailElements = [];
    for (let index = 0; index < longerDetailsLength; index++) {
        detailElements.push(
            <DetailRow
                key={index + 'TopDetail'}
                timeDetail={timeDetails.length > index ? timeDetails[index] : null}
                detail={details.length > index ? details[index] : null}
                icons={index === longerDetailsLength - 1 ? icons : null}
                buttonDetail={index === 0 ? buttonDetail : null}
                hasButton={index === 1 && !!buttonDetail}
                customStyles={customStyles}
            />
        );
    }
    return detailElements;
}

function getBottomDetailRows(details: BottomDetail[]): JSX.Element[] {
    const detailElements = [];
    for (let index = 0; index < details.length; index++) {
        detailElements.push(
            <div key={index + 'BottomDetail'} className={styles.detailRow}>
                {!details[index].isFullDetail && <div className={styles.leftColumn} />}
                <DetailPartView {...details[index]} />
            </div>
        );
    }
    return detailElements;
}
